<template>
  <Modal :title="modalTitle" :error="error" body-overflow="auto" @close="emit('close')">
    <div v-if="loading" class="muted">Loading…</div>
    <div v-else-if="!detail" class="muted">Details not found.</div>
    <div v-else class="objectDetailShell">
      <div class="objectDetailSection">
        <div class="objectDetailMetaRow">
          <span class="badge">{{ displayType }}</span>
          <code class="objectDetailCode">{{ normalizedObjectId }}</code>
        </div>
      </div>

      <div v-if="objectType === 'account'" class="objectDetailSection">
        <div class="objectDetailTitle">{{ detail.account?.title || detail.account?.company_name || detail.account?.name || 'Account' }}</div>
        <div v-if="detail.account?.summary" class="objectDetailText">{{ detail.account.summary }}</div>
        <div class="objectDetailGrid">
          <div class="objectDetailRow">
            <div class="objectDetailLabel">Domain</div>
            <div class="objectDetailValue">{{ detail.account?.domain || '—' }}</div>
          </div>
        </div>
        <div v-if="accountColumnRows.length" class="objectDetailGrid">
          <div v-for="row in accountColumnRows" :key="row.key" class="objectDetailRow">
            <div class="objectDetailLabel">{{ row.key }}</div>
            <div class="objectDetailValue">{{ row.value }}</div>
          </div>
        </div>
      </div>

      <div v-else-if="objectType === 'contact'" class="objectDetailSection">
        <div class="objectDetailTitle">{{ detail.display?.name || detail.salesforce?.Name || detail.email || 'Contact' }}</div>
        <div v-if="detail.display?.title || detail.salesforce?.Title" class="muted">{{ detail.display?.title || detail.salesforce?.Title }}</div>
        <div class="objectDetailGrid">
          <div class="objectDetailRow">
            <div class="objectDetailLabel">Email</div>
            <div class="objectDetailValue">{{ detail.email || '—' }}</div>
          </div>
          <div v-if="detail.buying_team" class="objectDetailRow">
            <div class="objectDetailLabel">Buying team role</div>
            <div class="objectDetailValue">{{ detail.buying_team.role || '—' }}</div>
          </div>
          <div v-if="detail.buying_team" class="objectDetailRow">
            <div class="objectDetailLabel">Engagement</div>
            <div class="objectDetailValue">{{ detail.buying_team.engagement_state || '—' }}</div>
          </div>
        </div>
        <div v-if="contactRows.length" class="objectDetailGrid">
          <div v-for="row in contactRows" :key="row.key" class="objectDetailRow">
            <div class="objectDetailLabel">{{ row.key }}</div>
            <div class="objectDetailValue">{{ row.value }}</div>
          </div>
        </div>
      </div>

      <div v-else-if="objectType === 'opportunity'" class="objectDetailSection">
        <div class="objectDetailTitle">{{ detail.Name || detail.name || 'Opportunity' }}</div>
        <div class="objectDetailGrid">
          <div v-for="row in genericRows" :key="row.key" class="objectDetailRow">
            <div class="objectDetailLabel">{{ row.key }}</div>
            <div class="objectDetailValue">{{ row.value }}</div>
          </div>
        </div>
      </div>

      <div v-else class="objectDetailGrid">
        <div v-for="row in genericRows" :key="row.key" class="objectDetailRow">
          <div class="objectDetailLabel">{{ row.key }}</div>
          <div class="objectDetailValue">{{ row.value }}</div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import Modal from './Modal.vue'
import { apiFetch } from '../services/api.ts'

const props = defineProps({
  objectType: { type: String, required: true },
  objectId: { type: [String, Number], required: true },
  accountId: { type: [String, Number], default: '' },
})

const emit = defineEmits(['close'])

const loading = ref(false)
const error = ref('')
const detail = ref(null)

const normalizedAccountId = computed(() => String(props.accountId || '').trim())
const normalizedObjectId = computed(() => String(props.objectId || '').trim())
const objectType = computed(() => String(props.objectType || '').trim().toLowerCase())
const displayType = computed(() => objectType.value || 'object')
const effectiveAccountId = computed(() => normalizedAccountId.value || normalizedObjectId.value)

const modalTitle = computed(() => {
  if (objectType.value === 'account') return detail.value?.account?.title || detail.value?.account?.company_name || detail.value?.account?.name || 'Account'
  if (objectType.value === 'contact') return detail.value?.display?.name || detail.value?.salesforce?.Name || detail.value?.email || 'Contact'
  if (objectType.value === 'opportunity') return detail.value?.Name || detail.value?.name || 'Opportunity'
  if (objectType.value === 'custom_object') return detail.value?.Name || detail.value?.name || detail.value?.object_type || 'Custom Object'
  return 'Details'
})

const accountColumnRows = computed(() => {
  const values = detail.value?.column_values
  if (!values || typeof values !== 'object') return []
  return Object.entries(values).map(([key, value]) => ({ key, value: formatValue(value) }))
})

const contactRows = computed(() => {
  if (!detail.value) return []
  const rows = []
  const display = detail.value.display && typeof detail.value.display === 'object' ? detail.value.display : {}
  const salesforce = detail.value.salesforce && typeof detail.value.salesforce === 'object' ? detail.value.salesforce : {}
  const buyingTeam = detail.value.buying_team && typeof detail.value.buying_team === 'object' ? detail.value.buying_team : {}
  for (const [key, value] of Object.entries({ ...display, ...salesforce, ...buyingTeam })) {
    if (['contact_id', 'url', 'display', 'id', 'is_buying_team_member'].includes(key)) continue
    rows.push({ key, value: formatValue(value) })
  }
  return rows
})

const genericRows = computed(() => {
  const raw = detail.value
  if (!raw || typeof raw !== 'object') return []
  return Object.entries(raw)
    .filter(([key]) => key !== 'attributes')
    .map(([key, value]) => ({ key, value: formatValue(value) }))
})

function formatValue(value) {
  if (value == null) return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

async function loadDetail() {
  const type = objectType.value
  const objectId = normalizedObjectId.value
  const accountId = effectiveAccountId.value
  detail.value = null
  error.value = ''
  if (!type || !objectId) return
  loading.value = true
  try {
    if (type === 'account') {
      const resp = await apiFetch(`/accounts/${encodeURIComponent(accountId)}/detail`)
      detail.value = resp
      return
    }
    if (!accountId) {
      throw new Error('Account context is required')
    }
    if (type === 'contact') {
      detail.value = await apiFetch(`/accounts/${encodeURIComponent(accountId)}/contacts/${encodeURIComponent(objectId)}`)
      return
    }
    if (type === 'opportunity') {
      const resp = await apiFetch(`/accounts/${encodeURIComponent(accountId)}/opportunities`)
      const rows = Array.isArray(resp?.opportunities) ? resp.opportunities : []
      detail.value = rows.find((row) => String(row?.Id || row?.id || '').trim() === objectId) || null
      return
    }
    if (type === 'custom_object') {
      const resp = await apiFetch(`/accounts/${encodeURIComponent(accountId)}/custom_objects`)
      const objects = resp?.objects && typeof resp.objects === 'object' ? resp.objects : {}
      for (const [objectName, rows] of Object.entries(objects)) {
        if (!Array.isArray(rows)) continue
        const match = rows.find((row) => String(row?.Id || row?.id || '').trim() === objectId)
        if (match) {
          detail.value = { ...match, object_type: objectName }
          return
        }
      }
      detail.value = null
      return
    }
  } catch (e) {
    error.value = e?.message || 'Failed to load details'
  } finally {
    loading.value = false
  }
}

watch([objectType, normalizedObjectId, normalizedAccountId], () => {
  void loadDetail()
}, { immediate: true })
</script>

<style scoped>
.objectDetailShell {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.objectDetailSection {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.objectDetailMetaRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.objectDetailCode {
  font-size: 12px;
  color: var(--muted);
}

.objectDetailTitle {
  font-size: 16px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.35;
}

.objectDetailText,
.objectDetailValue {
  color: var(--text);
  line-height: 1.5;
  white-space: pre-wrap;
}

.objectDetailGrid {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
}

.objectDetailRow {
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  gap: 12px;
}

.objectDetailRow:last-child {
  border-bottom: none;
}

.objectDetailLabel {
  font-size: 12px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
</style>
